import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Eye,
  ExternalLink,
  FileCheck2,
  KeyRound,
  RefreshCcw,
  ShieldOff,
  Wrench,
  XCircle,
  Zap,
} from 'lucide-react';
import { deploymentDashboardApi } from '../../services/deploymentApi';
import { useAuth } from '../../contexts/AuthContext';
import {
  DeploymentCustomer,
  DeploymentServer,
  DeploymentTargetRecord,
  ManagedArtifactStatus,
  ServerLock,
  SetupEventPayload,
  SetupRecord,
  SetupServerGroup,
  SetupStackSummary,
  SetupStatus,
  SetupTargetSummary,
  SetupTlsStatus,
  StackCandidate,
} from '../../types/deployment';
import { formatDeploymentModeLabel, resolvePreferredSetupMode } from '../../utils/deploymentMode';
import { SetupExplorer } from './SetupExplorer';
import { SetupRunHistoryState } from './SetupHistoryPanel';
import { formatManagedArtifactRequiredScope } from './setupExplorerUtils';

interface SetupPanelProps {
  targets: DeploymentTargetRecord[];
  onNotify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  onRefreshTargets: () => Promise<void>;
}

const STATUS_CONFIG: Record<SetupStatus, { label: string; color: 'success' | 'error' | 'warning' | 'default' | 'info'; icon: React.ReactNode }> = {
  requested: { label: 'Requested', color: 'default', icon: <Clock size={14} /> },
  running: { label: 'Running', color: 'info', icon: <CircularProgress size={14} /> },
  success: { label: 'Success', color: 'success', icon: <CheckCircle size={14} /> },
  failed: { label: 'Failed', color: 'error', icon: <XCircle size={14} /> },
  cancelled: { label: 'Cancelled', color: 'default', icon: <XCircle size={14} /> },
};

const SETUP_HISTORY_PAGE_SIZE = 10;

const getEntityId = (entity: { _id: string } | string | null | undefined): string => {
  if (!entity) return '';
  return typeof entity === 'string' ? entity : entity._id;
};

const getEntityLabel = (
  entity: { name?: string; slug?: string; hostname?: string } | string | null | undefined
): string => {
  if (!entity) return '—';
  return typeof entity === 'string' ? entity : entity.name || entity.slug || entity.hostname || '—';
};

const getSetupMetadata = (
  setup: SetupRecord | null | undefined
): { failureClass?: string; tlsStatus?: SetupTlsStatus | null } => (
  (setup?.metadata as { failureClass?: string; tlsStatus?: SetupTlsStatus | null } | undefined) ?? {}
);

const getFailureClass = (setup: SetupRecord | null | undefined): string => String(
  setup?.setupProgress?.failureClass
  || getSetupMetadata(setup).failureClass
  || ''
).trim();

const getFailureMessage = (setup: SetupRecord | null | undefined): string => String(
  setup?.failedStepMessage
  || setup?.errorMessage
  || ''
).trim();

const getSetupTlsStatus = (setup: SetupRecord | null | undefined): SetupTlsStatus | null => (
  getSetupMetadata(setup).tlsStatus ?? null
);

const FAILURE_CLASS_SUMMARY: Record<string, string> = {
  'lock-conflict': 'Another setup or deploy already holds the server lock.',
  'host-unreachable': 'The server could not be reached over SSH.',
  'missing-sudo': 'The deploy user does not have non-interactive sudo.',
  'low-disk': 'The target host does not have enough free disk.',
  'low-memory': 'The target host does not have enough free memory.',
  'port-conflict': 'A required host port is already in use.',
  'host-namespace-conflict': 'A host path, compose identifier, or Nginx namespace conflicts with another app.',
  'checkpoint-manifest-mismatch': 'The stored checkpoint no longer matches the current setup contract.',
  'checkpoint-resume-blocked': 'A previous interrupted setup run must be reconciled before retrying.',
  'healthcheck-failed': 'The service did not become healthy before setup timed out.',
  'missing-healthcheck': 'A required health-check URL is missing for the next stack phase.',
  'dependency-healthcheck-failed': 'A dependency target did not become healthy in time.',
  'rate-limited': 'A control-plane call was rate-limited and needs a retry.',
  'quota-exceeded': 'A control-plane quota or resource limit was exceeded.',
  'control-plane-unavailable': 'A control-plane dependency was temporarily unavailable.',
};

const formatFailureClassSummary = (failureClass: string): string => {
  if (!failureClass) {
    return '';
  }

  return FAILURE_CLASS_SUMMARY[failureClass]
    || failureClass.split('-').join(' ');
};

const getTargetFailureClass = (
  target: DeploymentTargetRecord | null | undefined,
  latestSetup: SetupRecord | null
): string => (
  getFailureClass(latestSetup)
  || String(target?.setupState?.lastFailureClass || '').trim()
);

const getTargetFailureMessage = (
  target: DeploymentTargetRecord | null | undefined,
  latestSetup: SetupRecord | null
): string => (
  getFailureMessage(latestSetup)
  || String(target?.setupState?.lastFailureMessage || '').trim()
);

const getTargetTlsStatus = (
  target: DeploymentTargetRecord | null | undefined,
  latestSetup: SetupRecord | null
): SetupTlsStatus | null => (
  getSetupTlsStatus(latestSetup)
  || target?.setupState?.tlsStatus
  || null
);

const shouldShowTlsStatus = (tlsStatus: SetupTlsStatus | null | undefined): boolean => (
  Boolean(tlsStatus)
  && tlsStatus?.managed !== false
  && tlsStatus?.state !== 'not-applicable'
);

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) {
    return '—';
  }

  const date = new Date(dateStr);
  return Number.isNaN(date.getTime()) ? String(dateStr) : date.toLocaleString();
};

const getTlsChipColor = (
  tlsStatus: SetupTlsStatus | null | undefined
): 'success' | 'warning' | 'info' | 'default' => {
  if (!tlsStatus) {
    return 'default';
  }

  if (tlsStatus.state === 'ready') {
    return 'success';
  }

  if (tlsStatus.state === 'warning') {
    return 'warning';
  }

  return 'info';
};

const formatTlsSummary = (tlsStatus: SetupTlsStatus | null | undefined): string => {
  if (!tlsStatus) {
    return '';
  }

  if (tlsStatus.summary) {
    return tlsStatus.summary;
  }

  const parts: string[] = [];
  if (tlsStatus.domain) {
    parts.push(`TLS ready for ${tlsStatus.domain}.`);
  }
  if (tlsStatus.certificateExpiresAt) {
    parts.push(`Certificate expires ${formatDateTime(tlsStatus.certificateExpiresAt)}.`);
  }
  if (tlsStatus.renewalSource) {
    parts.push(`Renewal source: ${tlsStatus.renewalSource}.`);
  }

  return parts.join(' ').trim();
};

type StackRunPhaseTargetView = {
  targetId?: string;
  targetName?: string;
  repositoryName?: string;
  selectedMode?: string;
  dryRunStatus?: string;
  dryRunSummary?: {
    label?: string;
    passCount?: number;
    warnCount?: number;
    failCount?: number;
    skipCount?: number;
    totalCount?: number;
  };
  dryRunHighlights?: string[];
};

type StackRunPhaseView = {
  index: number;
  name: string;
  status?: string;
  message?: string;
  failureClass?: string;
  failedTargetIds?: string[];
  summary?: {
    label?: string;
    passCount?: number;
    warnCount?: number;
    failCount?: number;
    skipCount?: number;
    totalCount?: number;
  };
  targets?: StackRunPhaseTargetView[];
};

type CredentialCopyEvent = NonNullable<SetupRecord['credentialCopyEvents']>[number];

const getStackRunPhases = (stackRun: SetupRecord): StackRunPhaseView[] => {
  const phases = stackRun.metadata?.phases;
  return Array.isArray(phases) ? phases : [];
};

const getSetupDryRunReport = (setup: SetupRecord | null | undefined) => setup?.metadata?.dryRun ?? null;

const getSetupDebugLog = (setup: SetupRecord | null | undefined) => {
  const debugLog = setup?.metadata?.debugLog;
  return Array.isArray(debugLog) ? debugLog : [];
};

const formatStructuredDetails = (value: unknown): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatDryRunSummary = (
  summary: {
    passCount?: number;
    warnCount?: number;
    failCount?: number;
    skipCount?: number;
    totalCount?: number;
    label?: string;
  } | null | undefined
): string => {
  if (!summary) {
    return '';
  }

  if (summary.label) {
    return summary.label;
  }

  return `${summary.passCount || 0} passed, ${summary.warnCount || 0} warnings, ${summary.failCount || 0} failed, ${summary.skipCount || 0} skipped`;
};

const getDryRunOutcome = (setup: SetupRecord | null | undefined): 'pass' | 'warn' | 'fail' | 'unknown' => {
  const summaryOutcome = String(getSetupDryRunReport(setup)?.summary?.outcome || '').trim().toLowerCase();
  if (summaryOutcome === 'warn' || summaryOutcome === 'fail' || summaryOutcome === 'pass') {
    return summaryOutcome;
  }

  if (setup?.status === 'failed') {
    return 'fail';
  }

  return 'unknown';
};

const getDryRunToast = (
  setup: SetupRecord | null | undefined,
  label: 'Setup dry-run' | 'Stack dry-run'
): { message: string; severity: 'success' | 'warning' } => {
  const outcome = getDryRunOutcome(setup);

  if (outcome === 'fail') {
    return {
      message: `${label} completed with failures`,
      severity: 'warning',
    };
  }

  if (outcome === 'warn') {
    return {
      message: `${label} completed with warnings`,
      severity: 'warning',
    };
  }

  if (outcome === 'unknown') {
    return {
      message: `${label} completed with unknown outcome`,
      severity: 'warning',
    };
  }

  return {
    message: `${label} completed`,
    severity: 'success',
  };
};

const getRequestFailureMessage = (
  error: unknown,
  fallbackMessage: string,
  timeoutMessage = 'The request timed out. The server may still be processing it, so the outcome is unknown. Check the latest status before trying again.'
): string => {
  const err = error as { code?: string; message?: string; response?: { data?: { message?: string } } };
  if (err?.response?.data?.message) {
    return err.response.data.message;
  }

  const errorCode = String(err?.code || '').trim();
  const errorMessage = String(err?.message || '').trim();
  if (errorCode === 'ECONNABORTED' || errorCode === 'ETIMEDOUT' || /timeout/i.test(errorMessage)) {
    return timeoutMessage;
  }

  return fallbackMessage;
};

const formatSetupActionLabel = (actionType: SetupRecord['actionType']): string => {
  const managedActionLabels: Partial<Record<SetupRecord['actionType'], string>> = {
    'managed-artifact-reconcile': 'Reconcile Managed Files',
    'certificate-check': 'Certificate Check',
    'tls-issue': 'Issue Certificate',
    'tls-renew-dry-run': 'Renewal Dry Run',
    'tls-renew': 'Renew If Due',
  };
  if (managedActionLabels[actionType]) {
    return managedActionLabels[actionType] as string;
  }
  if (actionType === 'dry-run') {
    return 'Dry Run';
  }
  if (actionType === 'stack-dry-run') {
    return 'Stack Dry Run';
  }
  if (actionType === 're-provision-credentials') {
    return 'Re-provision Credentials';
  }
  if (actionType === 'failed-preflight') {
    return 'Failed Preflight';
  }
  if (actionType === 'stack-run') {
    return 'Stack Run';
  }
  return actionType === 'check' ? 'Check' : 'Setup';
};

const getSetupStepChipColor = (
  status: string
): 'success' | 'error' | 'warning' | 'default' | 'info' => {
  if (status === 'success') {
    return 'success';
  }
  if (status === 'failed') {
    return 'error';
  }
  if (status === 'running') {
    return 'info';
  }
  if (status === 'skipped') {
    return 'warning';
  }
  return 'default';
};

const getDebugLogChipColor = (
  level: string
): 'success' | 'error' | 'warning' | 'default' | 'info' => {
  if (level === 'error') {
    return 'error';
  }
  if (level === 'warn') {
    return 'warning';
  }
  if (level === 'debug') {
    return 'default';
  }
  return 'info';
};

const getCredentialEventChipColor = (
  event: CredentialCopyEvent
): 'success' | 'info' | 'default' => {
  if (event.status === 'confirmed') {
    return 'success';
  }

  if (event.status === 'staged') {
    return 'info';
  }

  return 'default';
};

const getCredentialEventLabel = (event: CredentialCopyEvent): string => {
  if (event.status === 'confirmed') {
    return 'Credentials verified';
  }

  if (event.status === 'staged') {
    return 'Credentials staged';
  }

  return `Credential event via ${event.sourceType || 'setup'}`;
};

const getLatestCredentialEvent = (
  setup: SetupRecord | null | undefined
): CredentialCopyEvent | null => {
  const events = setup?.credentialCopyEvents ?? [];
  return events.length > 0 ? events[events.length - 1] : null;
};

const canTargetReprovisionCredentials = (target: DeploymentTargetRecord | null | undefined): boolean => {
  const server = target?.server && typeof target.server === 'object' ? target.server : null;
  const allowedTargetIds = server?.bootstrap?.allowedCredentialCopyTargetIds ?? [];
  return !!target && allowedTargetIds.includes(target._id);
};

const getTargetCredentialStatus = (
  target: DeploymentTargetRecord,
  latestSetup: SetupRecord | null,
): { label: string; color: 'success' | 'info' | 'warning'; detail: string } | null => {
  const latestEvent = getLatestCredentialEvent(latestSetup);
  if (latestEvent?.status === 'confirmed') {
    return {
      label: 'Credentials verified',
      color: 'success',
      detail: latestEvent.message
        || `Latest setup confirmed credentials on host at ${latestEvent.destinationPath}.`,
    };
  }

  if (latestEvent?.status === 'staged') {
    return {
      label: 'Credentials staged',
      color: 'info',
      detail: latestEvent.message
        || `Dashboard staged credentials at ${latestEvent.destinationPath}.`,
    };
  }

  const credentialsHostPath = String(target.gcpSetupProfile?.credentialsHostPath || '').trim();
  if (!credentialsHostPath) {
    return null;
  }

  if (canTargetReprovisionCredentials(target)) {
    return {
      label: 'Re-provision available',
      color: 'info',
      detail: `Dashboard can stage credentials to ${credentialsHostPath} before rerunning setup.`,
    };
  }

  return {
    label: 'Manual credential staging',
    color: 'warning',
    detail: `Stage credentials at ${credentialsHostPath} before rerunning setup because re-provision is not enabled for this target.`,
  };
};

const getTargetPrerequisiteMessages = (
  target: DeploymentTargetRecord,
  selectedMode: string,
): Array<{ severity: 'info' | 'warning'; message: string }> => {
  const messages: Array<{ severity: 'info' | 'warning'; message: string }> = [];
  const resolvedMode = selectedMode || resolvePreferredSetupMode(target);
  const server = typeof target.server === 'object' ? target.server : null;
  const composeFilePath = String(
    target.remoteSettings?.composeFilePath || server?.runtime?.composeFilePath || ''
  ).trim();
  const runtimeEnvFilePath = String(
    target.remoteSettings?.runtimeEnvFilePath || server?.runtime?.runtimeEnvFilePath || ''
  ).trim();
  const publicConfigTargetPath = String(
    target.pipelineVariables?.PUBLIC_RUNTIME_CONFIG_TARGET_PATH || ''
  ).trim();
  const restartCommand = String(
    target.remoteSettings?.remoteServiceRestartCommand || server?.runtime?.remoteServiceRestartCommand || ''
  ).trim();
  const credentialsHostPath = String(target.gcpSetupProfile?.credentialsHostPath || '').trim();

  if (credentialsHostPath) {
    messages.push({
      severity: canTargetReprovisionCredentials(target) ? 'info' : 'warning',
      message: canTargetReprovisionCredentials(target)
        ? `Setup verifies credentials at ${credentialsHostPath}. Use Re-provision if they need to be re-staged from the dashboard.`
        : `Setup verifies credentials at ${credentialsHostPath}. Stage them manually before rerunning because dashboard re-provision is not enabled for this target.`,
    });
  }

  if (resolvedMode === 'docker' && !composeFilePath) {
    messages.push({
      severity: 'warning',
      message: 'Docker mode expects a host-managed compose file path before setup/deploy can succeed.',
    });
  }

  if (['static', 'package', 'appimage'].includes(resolvedMode) && !runtimeEnvFilePath && !publicConfigTargetPath) {
    messages.push({
      severity: 'warning',
      message: `${formatDeploymentModeLabel(resolvedMode)} mode should expose either a runtime env file path or PUBLIC_RUNTIME_CONFIG_TARGET_PATH for generated runtime artifacts.`,
    });
  }

  if (resolvedMode === 'appimage' && !restartCommand) {
    messages.push({
      severity: 'warning',
      message: 'AppImage targets should define a remote restart command so setup reruns and deploys restart the service predictably.',
    });
  }

  return messages;
};

interface StartSetupDialogProps {
  open: boolean;
  target: DeploymentTargetRecord | null;
  latestSetup: SetupRecord | null;
  onClose: () => void;
  onConfirm: (
    targetId: string,
    mode: string,
    action: 'setup' | 'check' | 'dry-run',
    releaseSha?: string
  ) => Promise<void>;
  loading: boolean;
}

const StartSetupDialog: React.FC<StartSetupDialogProps> = ({ open, target, latestSetup, onClose, onConfirm, loading }) => {
  const [mode, setMode] = useState('');
  const [action, setAction] = useState<'setup' | 'check' | 'dry-run'>('setup');
  const [releaseSha, setReleaseSha] = useState('');

  useEffect(() => {
    if (open && target) {
      setAction('setup');
      setMode(resolvePreferredSetupMode(target));
      const metadataSha = typeof target.metadata?.selectedReleaseSha === 'string'
        ? target.metadata.selectedReleaseSha
        : '';
      const repositorySha = typeof target.repository === 'object'
        ? target.repository.versionSummary?.latestCommitSha || ''
        : '';
      setReleaseSha(metadataSha || repositorySha);
    }
  }, [open, target]);

  const allowedModes = target?.allowedDeploymentModes ?? [];
  const isSelectable = allowedModes.length > 1;
  const currentSetupStatus = target?.setupState?.status;
  const prerequisiteMessages = target ? getTargetPrerequisiteMessages(target, mode) : [];
  const latestFailureClass = getTargetFailureClass(target, latestSetup);
  const latestFailureMessage = getTargetFailureMessage(target, latestSetup);
  const credentialStatus = target ? getTargetCredentialStatus(target, latestSetup) : null;
  const tlsStatus = getTargetTlsStatus(target, latestSetup);
  const releaseShaIsValid = !releaseSha.trim() || /^[a-f0-9]{40}$/i.test(releaseSha.trim());

  const handleConfirm = () => {
    if (!target) return;
    onConfirm(target._id, mode, action, action === 'check' ? undefined : releaseSha.trim() || undefined);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <Wrench size={20} />
          {action === 'setup' ? 'Run Setup' : action === 'check' ? 'Run Preflight Check' : 'Run Dry Run'}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {target && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Target: <strong>{target.name}</strong> — {target.environment}
            </Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={action}
                label="Action"
                onChange={(e) => setAction(e.target.value as 'setup' | 'check' | 'dry-run')}
              >
                <MenuItem value="setup">Setup — install prerequisites and bootstrap host</MenuItem>
                <MenuItem value="check">Check — preflight only, no host mutation</MenuItem>
                <MenuItem value="dry-run">Dry Run — validate metadata, SSH, paths, and host prerequisites</MenuItem>
              </Select>
            </FormControl>

            {action !== 'check' && (
              <TextField
                label="Expected release commit SHA"
                value={releaseSha}
                onChange={(event) => setReleaseSha(event.target.value)}
                error={!releaseShaIsValid}
                helperText={releaseShaIsValid
                  ? 'Optional. Leave blank to resolve the current default-branch head; a supplied SHA must still match the setup pipeline.'
                  : 'Enter the full 40-character commit SHA.'}
                inputProps={{ spellCheck: false }}
                fullWidth
                size="small"
              />
            )}

            {isSelectable && (
              <FormControl fullWidth size="small">
                <InputLabel>Deployment Mode</InputLabel>
                <Select
                  value={mode}
                  label="Deployment Mode"
                  onChange={(e) => setMode(e.target.value)}
                >
                  {allowedModes.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m === 'docker' && 'Docker / Compose'}
                      {m === 'static' && 'Static frontend (Nginx file serving)'}
                      {m === 'package' && 'Package / Node.js service'}
                      {m === 'appimage' && 'AppImage (Electron/robot)'}
                      {!['docker', 'static', 'package', 'appimage'].includes(m) && m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {latestFailureClass && (
              <Alert severity="warning" icon={<AlertCircle size={16} />}>
                {formatFailureClassSummary(latestFailureClass)}
                {latestFailureClass ? ` (${latestFailureClass})` : ''}
                {latestFailureMessage ? ` ${latestFailureMessage}` : ''}
              </Alert>
            )}

            {shouldShowTlsStatus(tlsStatus) && (
              <Alert
                severity={getTlsChipColor(tlsStatus) === 'success' ? 'success' : getTlsChipColor(tlsStatus) === 'warning' ? 'warning' : 'info'}
                icon={getTlsChipColor(tlsStatus) === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              >
                {formatTlsSummary(tlsStatus)}
              </Alert>
            )}

            {credentialStatus && (
              <Alert
                severity={credentialStatus.color === 'success' ? 'success' : credentialStatus.color === 'warning' ? 'warning' : 'info'}
                icon={<KeyRound size={16} />}
              >
                {credentialStatus.detail}
              </Alert>
            )}

            {prerequisiteMessages.map((item) => (
              <Alert key={`${item.severity}-${item.message}`} severity={item.severity} icon={<AlertCircle size={16} />}>
                {item.message}
              </Alert>
            ))}

            {['ready', 'ready-for-update'].includes(currentSetupStatus || '') && action === 'setup' && (
              <Alert severity="info" icon={<CheckCircle size={16} />}>
                This target was already set up. Running again will skip completed steps and re-apply any
                missing configuration.
              </Alert>
            )}

            {['failed', 'partially-ready'].includes(currentSetupStatus || '') && (
              <Alert severity="warning" icon={<AlertCircle size={16} />}>
                Previous setup failed at step: <strong>{target.setupState?.lastFailedStep || 'unknown'}</strong>.
                This run will resume from the last incomplete step.
              </Alert>
            )}

            {currentSetupStatus === 'failed-preflight' && (
              <Alert severity="warning" icon={<AlertCircle size={16} />}>
                The last preflight failed before any host mutation. Review the missing prerequisite and rerun
                the check or setup.
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || (!mode && isSelectable) || (action !== 'check' && !releaseShaIsValid)}
          startIcon={loading ? <CircularProgress size={16} /> : <Zap size={16} />}
        >
          {action === 'setup' ? 'Run Setup' : action === 'check' ? 'Run Check' : 'Run Dry Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ForceReleaseDialogProps {
  open: boolean;
  lock: ServerLock | null;
  onClose: () => void;
  onConfirm: (lockId: string, reason: string) => Promise<void>;
  loading: boolean;
}

const ForceReleaseDialog: React.FC<ForceReleaseDialogProps> = ({ open, lock, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1} color="error.main">
          <ShieldOff size={20} />
          Force-Release Server Lock
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning">
            This will release the lock without waiting for the GitLab pipeline to finish.
            Only use this for orphaned or stuck locks after verifying the pipeline is no longer active.
          </Alert>
          {lock && (
            <Typography variant="body2">
              Lock held by <strong>{lock.owner?.name || lock.owner?.email || 'unknown'}</strong>
              {' '}since {new Date(lock.acquiredAt).toLocaleString()}.
              Run type: <strong>{lock.runType}</strong>.
              Health: <strong>{lock.healthState}</strong>.
            </Typography>
          )}
          <TextField
            label="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Explain why you are force-releasing this lock"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={() => lock && onConfirm(lock._id, reason)}
          variant="contained"
          color="error"
          disabled={loading || !reason.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <ShieldOff size={16} />}
        >
          Force Release
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface StartStackDialogProps {
  open: boolean;
  stack: StackCandidate | null;
  onClose: () => void;
  onConfirm: (payload: {
    action: 'stack-run' | 'stack-dry-run';
    stackId: string;
    customerId: string;
    serverId: string;
    environment: string;
    modeOverrides: Record<string, string>;
    reason: string;
  }) => Promise<void>;
  loading: boolean;
}

const StartStackDialog: React.FC<StartStackDialogProps> = ({ open, stack, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  const [modeOverrides, setModeOverrides] = useState<Record<string, string>>({});
  const [action, setAction] = useState<'stack-run' | 'stack-dry-run'>('stack-run');

  const selectableTargets = useMemo(
    () => (stack?.phases ?? []).flatMap((phase) => phase.targets).filter((target) => target.allowedModes.length > 1),
    [stack]
  );

  useEffect(() => {
    if (!stack) {
      setReason('');
      setModeOverrides({});
      setAction('stack-run');
      return;
    }

    const defaults = selectableTargets.reduce<Record<string, string>>((acc, target) => {
      acc[target.targetId] = target.defaultMode || target.allowedModes[0] || '';
      return acc;
    }, {});

    setReason('');
    setModeOverrides(defaults);
    setAction('stack-run');
  }, [stack, selectableTargets]);

  const handleConfirm = () => {
    if (!stack) return;
    onConfirm({
      action,
      stackId: stack.stackId,
      customerId: getEntityId(stack.customer),
      serverId: getEntityId(stack.server),
      environment: stack.environment,
      modeOverrides,
      reason,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack spacing={0.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Zap size={18} />
            <Typography variant="h6">{stack?.label || (action === 'stack-dry-run' ? 'Start Stack Dry Run' : 'Start Stack Run')}</Typography>
          </Stack>
          {stack && (
            <Typography variant="body2" color="text.secondary">
              {getEntityLabel(stack.customer)} · {getEntityLabel(stack.server)} · {stack.environment}
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {stack && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Execution</InputLabel>
              <Select
                value={action}
                label="Execution"
                onChange={(event) => setAction(event.target.value as 'stack-run' | 'stack-dry-run')}
              >
                <MenuItem value="stack-run">Stack Run — execute setup phase by phase</MenuItem>
                <MenuItem value="stack-dry-run">Stack Dry Run — validate every phase without host mutation</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info">
              {action === 'stack-dry-run'
                ? 'Stack dry-runs validate every phase and target in order, but they do not create pipelines, acquire locks, or mutate hosts.'
                : 'Stack runs execute phase by phase. Every target in the current phase must finish before the next phase starts.'}
            </Alert>

            <Stack spacing={1}>
              {stack.phases.map((phase) => (
                <Card key={`${stack.stackId}-${phase.index}`} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {phase.name}
                    </Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {phase.targets.map((target) => (
                        <Chip
                          key={target.targetId}
                          label={`${target.targetName} (${target.repositoryName})`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            {selectableTargets.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">Mode overrides</Typography>
                {selectableTargets.map((target) => (
                  <FormControl key={target.targetId} fullWidth size="small">
                    <InputLabel>{target.targetName}</InputLabel>
                    <Select
                      value={modeOverrides[target.targetId] || target.defaultMode || ''}
                      label={target.targetName}
                      onChange={(event) => setModeOverrides((current) => ({
                        ...current,
                        [target.targetId]: event.target.value,
                      }))}
                    >
                      {target.allowedModes.map((mode) => (
                        <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
              </Stack>
            )}

            <TextField
              label="Reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              fullWidth
              size="small"
              multiline
              minRows={2}
              placeholder="Optional operator note for this stack run"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !stack}
          startIcon={loading ? <CircularProgress size={16} /> : <Zap size={16} />}
        >
          {action === 'stack-dry-run' ? 'Run Stack Dry Run' : 'Start Stack Run'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface SetupRunDetailsDialogProps {
  open: boolean;
  setup: SetupRecord | null;
  loading: boolean;
  onClose: () => void;
  onRefresh: () => Promise<void>;
}

const SetupRunDetailsDialog: React.FC<SetupRunDetailsDialogProps> = ({
  open,
  setup,
  loading,
  onClose,
  onRefresh,
}) => {
  const target = setup?.deploymentTarget && typeof setup.deploymentTarget === 'object'
    ? setup.deploymentTarget
    : null;
  const repository = setup?.repository && typeof setup.repository === 'object'
    ? setup.repository
    : null;
  const server = setup?.server && typeof setup.server === 'object'
    ? setup.server
    : null;
  const statusCfg = STATUS_CONFIG[setup?.status ?? 'requested'] ?? STATUS_CONFIG.requested;
  const dryRunReport = getSetupDryRunReport(setup);
  const tlsStatus = getSetupTlsStatus(setup);
  const phaseStates = setup ? getStackRunPhases(setup) : [];
  const debugLog = setup ? [...getSetupDebugLog(setup)].reverse() : [];
  const credentialEvents = setup?.credentialCopyEvents ?? [];
  const managedHostFileEvents = setup?.managedHostFileEvents ?? [];
  const steps = setup?.steps ?? [];
  const isStackRecord = ['stack-run', 'stack-dry-run'].includes(setup?.actionType || '');
  const primaryFailureMessage = getFailureMessage(setup);
  const primaryFailureClass = getFailureClass(setup);
  const hasGitLabData = Boolean(
    setup?.gitlab?.projectId
    || setup?.gitlab?.pipelineId
    || setup?.gitlab?.jobId
    || setup?.gitlab?.jobName
    || setup?.gitlab?.pipelineWebUrl
    || setup?.gitlab?.jobWebUrl
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <Stack spacing={0.5}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Eye size={18} />
              <Typography variant="h6">Run Details</Typography>
            </Stack>
            {setup && (
              <Typography variant="body2" color="text.secondary">
                {isStackRecord
                  ? String(setup.metadata?.stackLabel || 'Stack run')
                  : `${getEntityLabel(target || setup.deploymentTarget)} · ${setup.environment}`}
              </Typography>
            )}
          </Stack>
          <Button
            onClick={() => { void onRefresh(); }}
            size="small"
            variant="outlined"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshCcw size={14} />}
            disabled={loading || !setup}
          >
            Reload
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : !setup ? (
          <Alert severity="warning">Setup details could not be loaded.</Alert>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                icon={statusCfg.icon as React.ReactElement}
                label={statusCfg.label}
                size="small"
                color={statusCfg.color}
              />
              <Chip label={formatSetupActionLabel(setup.actionType)} size="small" variant="outlined" />
              {setup.setupMode ? (
                <Chip label={formatDeploymentModeLabel(setup.setupMode)} size="small" variant="outlined" />
              ) : null}
              <Chip label={setup.environment} size="small" variant="outlined" />
              {primaryFailureClass ? (
                <Chip label={`Failure: ${primaryFailureClass}`} size="small" color="warning" variant="outlined" />
              ) : null}
            </Stack>

            {(primaryFailureMessage || primaryFailureClass) && (
              <Alert severity={setup.status === 'failed' ? 'error' : 'warning'} icon={<AlertCircle size={16} />}>
                {primaryFailureMessage || 'This run recorded a failure.'}
                {primaryFailureClass ? ` (${primaryFailureClass})` : ''}
              </Alert>
            )}

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Summary
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">Target / Stack</Typography>
                    <Typography variant="body2">
                      {isStackRecord
                        ? String(setup.metadata?.stackLabel || 'Stack run')
                        : getEntityLabel(target || setup.deploymentTarget)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Server</Typography>
                    <Typography variant="body2">{getEntityLabel(server || setup.server)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Repository</Typography>
                    <Typography variant="body2">
                      {repository?.pathWithNamespace || getEntityLabel(repository || setup.repository)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Job Name</Typography>
                    <Typography variant="body2">{setup.setupJobName || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Current Step</Typography>
                    <Typography variant="body2">{setup.currentStep || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Failed Step</Typography>
                    <Typography variant="body2">{setup.failedStep || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Started</Typography>
                    <Typography variant="body2">{formatDateTime(setup.startedAt ?? setup.createdAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Completed</Typography>
                    <Typography variant="body2">{formatDateTime(setup.completedAt)}</Typography>
                  </Box>
                  <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                    <Typography variant="caption" color="text.secondary">Correlation ID</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{setup.correlationId}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {tlsStatus && tlsStatus.state !== 'not-applicable' ? (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    TLS Status
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1.25,
                    }}
                  >
                    <Typography variant="body2">Domain: {tlsStatus.domain || '—'}</Typography>
                    <Typography variant="body2">Expires: {formatDateTime(tlsStatus.certificateExpiresAt)}</Typography>
                    <Typography variant="body2">Issuer: {tlsStatus.issuer || '—'}</Typography>
                    <Typography variant="body2">Renewal: {tlsStatus.renewalSource || '—'} / {tlsStatus.renewalTimerStatus || 'unknown'}</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Active fingerprint: {tlsStatus.activeCertificateFingerprint || '—'}</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Nginx fingerprint: {tlsStatus.nginxObservedFingerprint || '—'}</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Broker fingerprint: {tlsStatus.brokerObservedFingerprint || '—'}</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>Dashboard fingerprint: {tlsStatus.dashboardObservedFingerprint || '—'}</Typography>
                    <Typography variant="body2">Last renewal: {formatDateTime(tlsStatus.lastRenewalAt)}</Typography>
                    <Typography variant="body2">Last dry-run: {tlsStatus.lastDryRunResult || '—'} / {formatDateTime(tlsStatus.lastDryRunAt)}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ) : null}

            {hasGitLabData && setup.gitlab && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    GitLab
                  </Typography>
                  <Stack spacing={0.75}>
                    <Typography variant="body2">Project ID: {setup.gitlab.projectId ?? '—'}</Typography>
                    <Typography variant="body2">Pipeline ID: {setup.gitlab.pipelineId ?? '—'}</Typography>
                    <Typography variant="body2">Job ID: {setup.gitlab.jobId ?? '—'}</Typography>
                    <Typography variant="body2">Job Name: {setup.gitlab.jobName || '—'}</Typography>
                    <Typography variant="body2">Job Status: {setup.gitlab.jobStatus || '—'}</Typography>
                    <Typography variant="body2">Ref: {setup.gitlab.ref || '—'}</Typography>
                    {setup.gitlab.jobWebUrl ? (
                      <Typography
                        component="a"
                        href={setup.gitlab.jobWebUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        color="primary"
                        sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        Open job
                        <ExternalLink size={12} />
                      </Typography>
                    ) : null}
                    {setup.gitlab.pipelineWebUrl ? (
                      <Typography
                        component="a"
                        href={setup.gitlab.pipelineWebUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        color="primary"
                        sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        Open pipeline
                        <ExternalLink size={12} />
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {credentialEvents.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Credential Events
                  </Typography>
                  <Stack spacing={1.25}>
                    {credentialEvents.map((event, index) => (
                      <Box
                        key={`${setup._id}-detail-credential-${index}`}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                      >
                        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 0.75 }}>
                          <Chip
                            size="small"
                            color={getCredentialEventChipColor(event)}
                            label={getCredentialEventLabel(event)}
                          />
                          <Chip size="small" variant="outlined" label={event.sourceType || 'setup'} />
                        </Stack>
                        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                          Destination: {event.destinationPath || '—'}
                        </Typography>
                        {event.sourcePath && (
                          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                            Source: {event.sourcePath}
                          </Typography>
                        )}
                        {event.message && (
                          <Typography variant="body2" color="text.secondary">
                            {event.message}
                          </Typography>
                        )}
                        {(event.confirmationSource || event.confirmedAt) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {event.confirmationSource ? `Confirmed by ${event.confirmationSource}` : 'Confirmed'}
                            {event.confirmedAt ? ` · ${formatDateTime(event.confirmedAt)}` : ''}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {managedHostFileEvents.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Managed File Events
                  </Typography>
                  <Stack spacing={1.25}>
                    {managedHostFileEvents.map((event, index) => (
                      <Box
                        key={`${setup._id}-managed-file-${event.setKey}-${event.fileKey || index}`}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                      >
                        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                          <Chip size="small" label={event.status || 'unknown'} />
                          <Chip size="small" variant="outlined" label={event.setKey} />
                          {event.fileKey ? <Chip size="small" variant="outlined" label={event.fileKey} /> : null}
                        </Stack>
                        {event.destinationPath ? (
                          <Typography variant="body2" sx={{ mt: 0.75, overflowWrap: 'anywhere' }}>
                            Destination: {event.destinationPath}
                          </Typography>
                        ) : null}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {event.sourceType || 'managed source'}
                          {event.referenceKey ? ` · ${event.referenceKey}` : ''}
                          {event.resolvedVersion ? ` · version ${event.resolvedVersion}` : ''}
                          {event.mode ? ` · mode ${event.mode}` : ''}
                        </Typography>
                        {event.message ? (
                          <Typography variant="body2" color="text.secondary">{event.message}</Typography>
                        ) : null}
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {steps.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Setup Steps
                  </Typography>
                  <Stack spacing={1.25}>
                    {steps.map((step, index) => (
                      <Box
                        key={`${setup._id}-detail-step-${step.name}-${index}`}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                      >
                        <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
                          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" fontWeight={600}>{step.name}</Typography>
                            <Chip
                              size="small"
                              color={getSetupStepChipColor(step.status)}
                              label={step.status || 'pending'}
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {step.startedAt ? formatDateTime(step.startedAt) : 'Not started'}
                            {step.completedAt ? ` -> ${formatDateTime(step.completedAt)}` : ''}
                          </Typography>
                        </Stack>
                        {step.message && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, overflowWrap: 'anywhere' }}>
                            {step.message}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {phaseStates.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Stack Phases
                  </Typography>
                  <Stack spacing={1.25}>
                    {phaseStates.map((phase) => (
                      <Box
                        key={`${setup._id}-detail-phase-${phase.index}`}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                      >
                        <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
                          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body2" fontWeight={600}>{phase.name}</Typography>
                            <Chip
                              size="small"
                              color={phase.status === 'failed' ? 'error' : phase.status === 'success' ? 'success' : phase.status === 'running' ? 'info' : 'default'}
                              label={phase.status || 'pending'}
                            />
                            {phase.failureClass ? (
                              <Chip size="small" variant="outlined" color="warning" label={phase.failureClass} />
                            ) : null}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {phase.summary?.label || ''}
                          </Typography>
                        </Stack>
                        {phase.message && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, overflowWrap: 'anywhere' }}>
                            {phase.message}
                          </Typography>
                        )}
                        {(phase.targets ?? []).length > 0 && (
                          <Stack spacing={0.5} sx={{ mt: 1 }}>
                            {(phase.targets ?? []).map((phaseTarget) => (
                              <Typography
                                key={`${setup._id}-detail-phase-${phase.index}-target-${phaseTarget.targetId || phaseTarget.repositoryName}`}
                                variant="caption"
                                color="text.secondary"
                                sx={{ overflowWrap: 'anywhere' }}
                              >
                                {phaseTarget.targetName || phaseTarget.repositoryName || phaseTarget.targetId || 'target'}
                                {phaseTarget.selectedMode ? ` (${phaseTarget.selectedMode})` : ''}
                                {phaseTarget.dryRunSummary?.label ? ` — ${phaseTarget.dryRunSummary.label}` : ''}
                              </Typography>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {dryRunReport && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Dry Run Summary
                  </Typography>
                  <Typography variant="body2">
                    {formatDryRunSummary(dryRunReport.summary)}
                  </Typography>
                  {(dryRunReport.highlights || []).length > 0 && (
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {(dryRunReport.highlights || []).map((highlight) => (
                        <Typography key={`${setup._id}-highlight-${highlight}`} variant="caption" color="text.secondary">
                          {highlight}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  {(dryRunReport.checks || []).length > 0 && (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                      {(dryRunReport.checks || []).map((check) => (
                        <Box
                          key={`${setup._id}-dry-run-check-${check.key}`}
                          sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                        >
                          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Chip
                              size="small"
                              color={check.status === 'fail' ? 'error' : check.status === 'warn' ? 'warning' : check.status === 'pass' ? 'success' : 'default'}
                              label={check.status}
                            />
                            <Chip size="small" variant="outlined" label={check.category} />
                            <Typography variant="body2" fontWeight={600}>{check.label}</Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            {check.message}
                          </Typography>
                          {check.detail ? (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              {check.detail}
                            </Typography>
                          ) : null}
                        </Box>
                      ))}
                    </Stack>
                  )}
                  {(dryRunReport.targets || []).map((targetResult) => (
                    <Box key={`${setup._id}-dry-run-target-${targetResult.targetId}`} sx={{ mt: 1.5 }}>
                      <Typography variant="subtitle2">
                        {targetResult.targetName}
                        {targetResult.repositoryName ? ` · ${targetResult.repositoryName}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDryRunSummary(targetResult.summary)}
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {(targetResult.checks || []).map((check) => (
                          <Box
                            key={`${setup._id}-${targetResult.targetId}-${check.key}`}
                            sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                          >
                            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                              <Chip
                                size="small"
                                color={check.status === 'fail' ? 'error' : check.status === 'warn' ? 'warning' : check.status === 'pass' ? 'success' : 'default'}
                                label={check.status}
                              />
                              <Typography variant="body2" fontWeight={600}>{check.label}</Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                              {check.message}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Control Plane Logs
                </Typography>
                {debugLog.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No control-plane logs were captured for this run.
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {debugLog.map((entry, index) => (
                      <Box
                        key={`${setup._id}-debug-log-${entry.ts}-${index}`}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                      >
                        <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap" alignItems="center">
                          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Chip
                              size="small"
                              color={getDebugLogChipColor(entry.level)}
                              label={String(entry.level || 'info').toUpperCase()}
                            />
                            {entry.event ? (
                              <Chip size="small" variant="outlined" label={entry.event} />
                            ) : null}
                            <Typography variant="body2" fontWeight={600}>
                              {entry.message}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(entry.ts)}
                          </Typography>
                        </Stack>
                        {entry.details !== undefined && entry.details !== null && (
                          <Box
                            component="pre"
                            sx={{
                              mt: 1,
                              mb: 0,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: 'background.default',
                              overflowX: 'auto',
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere',
                              fontSize: 12,
                              fontFamily: 'monospace',
                            }}
                          >
                            {formatStructuredDetails(entry.details)}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

interface ManagedArtifactsDialogProps {
  open: boolean;
  target: DeploymentTargetRecord | null;
  artifacts: ManagedArtifactStatus[];
  loading: boolean;
  actionLoading: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onReload: () => Promise<void>;
  onReconcile: () => Promise<void>;
}

const ManagedArtifactsDialog: React.FC<ManagedArtifactsDialogProps> = ({
  open,
  target,
  artifacts,
  loading,
  actionLoading,
  isAdmin,
  onClose,
  onReload,
  onReconcile,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Stack spacing={0.5}>
          <Stack direction="row" gap={1} alignItems="center">
            <FileCheck2 size={18} />
            <Typography variant="h6">Managed Files</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {target?.name || 'Deployment target'}
          </Typography>
          {target ? (
            <Typography variant="caption" color="text.secondary">
              Exact scope: {getEntityLabel(target.customer)} / {getEntityLabel(target.repository)} / {getEntityLabel(target.server)} / {target.environment}
            </Typography>
          ) : null}
        </Stack>
        <Button
          size="small"
          variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshCcw size={14} />}
          disabled={loading || actionLoading || !target}
          onClick={() => { void onReload(); }}
        >
          Refresh
        </Button>
      </Stack>
    </DialogTitle>
    <DialogContent dividers>
      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>
      ) : artifacts.length === 0 ? (
        <Alert severity="info">
          This target does not declare any managed host files at its selected release.
        </Alert>
      ) : (
        <Stack spacing={2}>
          {artifacts.map((artifact) => (
            <Card key={artifact.key} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle1" fontWeight={700}>{artifact.key}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                      {artifact.hostDirectory}
                    </Typography>
                  </Stack>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Chip size="small" label={artifact.status || 'unknown'} />
                    {artifact.activeRevision ? <Chip size="small" variant="outlined" label={`active ${artifact.activeRevision}`} /> : null}
                    {artifact.lastReconciledAt ? (
                      <Chip size="small" variant="outlined" label={`reconciled ${formatDateTime(artifact.lastReconciledAt)}`} />
                    ) : null}
                  </Stack>
                </Stack>
                {(artifact.restartServices || []).length > 0 ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Restart services: {(artifact.restartServices || []).join(', ')}
                  </Typography>
                ) : null}
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                  {artifact.files.map((file) => (
                    <Box
                      key={`${artifact.key}-${file.key}`}
                      sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: 1.25 }}
                    >
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        <Typography variant="body2" fontWeight={600}>{file.key}</Typography>
                        <Chip size="small" label={file.status || 'unknown'} />
                        <Chip size="small" variant="outlined" label={file.source.type} />
                        <Chip size="small" variant="outlined" label={`mode ${file.mode}`} />
                        {file.required ? <Chip size="small" color="warning" variant="outlined" label="required" /> : null}
                        {file.initializationSensitive ? (
                          <Chip size="small" color="info" variant="outlined" label="initialization-sensitive" />
                        ) : null}
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.75, overflowWrap: 'anywhere' }}>
                        Destination: {file.destination}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {file.source.referenceKey
                          ? `Reference: ${file.source.referenceKey}`
                          : `Repository: ${file.source.path || 'selected release'}`}
                        {file.resolvedVersion ? ` · resolved ${file.resolvedVersion}` : ''}
                      </Typography>
                      {file.requiredScope ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>
                          Required scope: {formatManagedArtifactRequiredScope(file.requiredScope)}
                        </Typography>
                      ) : null}
                      {file.message ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {file.message}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
      {isAdmin ? (
        <Button
          variant="contained"
          startIcon={actionLoading ? <CircularProgress size={14} /> : <FileCheck2 size={14} />}
          disabled={actionLoading || loading || !target}
          onClick={() => { void onReconcile(); }}
        >
          Reconcile Managed Files
        </Button>
      ) : null}
    </DialogActions>
  </Dialog>
);

export const SetupPanel: React.FC<SetupPanelProps> = ({ targets, onNotify, onRefreshTargets }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [serverGroups, setServerGroups] = useState<SetupServerGroup[]>([]);
  const [targetHistoryById, setTargetHistoryById] = useState<Record<string, SetupRunHistoryState>>({});
  const [stackHistoryByKey, setStackHistoryByKey] = useState<Record<string, SetupRunHistoryState>>({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const refreshTimerRef = useRef<number | null>(null);
  const activeDetailsIdRef = useRef<string | null>(null);

  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<DeploymentTargetRecord | null>(null);
  const [managedArtifactsDialogOpen, setManagedArtifactsDialogOpen] = useState(false);
  const [managedArtifactsTarget, setManagedArtifactsTarget] = useState<DeploymentTargetRecord | null>(null);
  const [managedArtifacts, setManagedArtifacts] = useState<ManagedArtifactStatus[]>([]);
  const [managedArtifactsLoading, setManagedArtifactsLoading] = useState(false);
  const activeManagedTargetIdRef = useRef<string | null>(null);
  const managedArtifactsRequestRef = useRef(0);

  useEffect(() => () => {
    activeManagedTargetIdRef.current = null;
    managedArtifactsRequestRef.current += 1;
  }, []);

  const [forceReleaseDialogOpen, setForceReleaseDialogOpen] = useState(false);
  const [selectedLock, setSelectedLock] = useState<ServerLock | null>(null);

  const [stackDialogOpen, setStackDialogOpen] = useState(false);
  const [selectedStack, setSelectedStack] = useState<StackCandidate | null>(null);

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRunDetails, setSelectedRunDetails] = useState<SetupRecord | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const latestSetupByTargetId = useMemo(() => {
    const entries = new Map<string, SetupRecord>();
    serverGroups.forEach((group) => {
      group.targetSummaries.forEach((summary) => {
        if (summary.latestSetup) {
          entries.set(summary.targetId, summary.latestSetup);
        }
      });
    });
    return entries;
  }, [serverGroups]);

  const availableCustomers = useMemo(() => {
    const entries = new Map<string, DeploymentCustomer>();
    targets.forEach((target) => {
      if (target.customer && typeof target.customer === 'object') {
        entries.set(target.customer._id, target.customer);
      }
    });
    return Array.from(entries.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [targets]);

  const availableServers = useMemo(() => {
    const entries = new Map<string, DeploymentServer>();
    targets.forEach((target) => {
      if (target.server && typeof target.server === 'object') {
        entries.set(target.server._id, target.server);
      }
    });
    return Array.from(entries.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [targets]);

  const selectedTargetLatestSetup = useMemo(
    () => (selectedTarget ? latestSetupByTargetId.get(selectedTarget._id) || null : null),
    [latestSetupByTargetId, selectedTarget]
  );

  const loadData = useCallback(async (background = false) => {
    if (!background) {
      setLoading(true);
    }
    setError(null);
    try {
      const serverGroupsRes = await deploymentDashboardApi.getSetupServerGroups({ limit: 100 });
      setServerGroups(serverGroupsRes.data ?? []);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load setup data';
      setError(msg);
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, []);

  const refreshTargetsAndData = useCallback(async (background = false) => {
    await Promise.allSettled([
      loadData(background),
      onRefreshTargets(),
    ]);
  }, [loadData, onRefreshTargets]);

  useEffect(() => {
    void refreshTargetsAndData();
  }, [refreshTargetsAndData]);

  const scheduleBackgroundRefresh = useCallback(() => {
    if (refreshTimerRef.current !== null) {
      return;
    }

    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null;
      void loadData(true);
    }, 800);
  }, [loadData]);

  useEffect(() => {
    const stream = deploymentDashboardApi.createSetupEventStream();

    const handleStreamEvent = (payload: SetupEventPayload) => {
      if (!payload?.type) {
        return;
      }
      scheduleBackgroundRefresh();
    };

    const onInit = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as { events?: SetupEventPayload[] };
        if (Array.isArray(payload.events) && payload.events.length > 0) {
          scheduleBackgroundRefresh();
        }
      } catch {
        scheduleBackgroundRefresh();
      }
    };

    const onUpdate = (event: MessageEvent) => {
      try {
        handleStreamEvent(JSON.parse(event.data) as SetupEventPayload);
      } catch {
        scheduleBackgroundRefresh();
      }
    };

    stream.addEventListener('init', onInit as EventListener);
    stream.addEventListener('setup-update', onUpdate as EventListener);
    stream.onerror = () => {
      scheduleBackgroundRefresh();
    };

    return () => {
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      stream.close();
    };
  }, [scheduleBackgroundRefresh]);

  const handleStartSetup = (target: DeploymentTargetRecord) => {
    setSelectedTarget(target);
    setSetupDialogOpen(true);
  };

  const handleConfirmSetup = async (
    targetId: string,
    mode: string,
    action: 'setup' | 'check' | 'dry-run',
    releaseSha?: string
  ) => {
    setActionLoading(true);
    try {
      let setupResponse;
      if (action === 'dry-run') {
        setupResponse = await deploymentDashboardApi.createSetupDryRun({
          deploymentTargetId: targetId,
          setupMode: mode,
          releaseSha,
        });
      } else {
        setupResponse = await deploymentDashboardApi.createSetup({
          deploymentTargetId: targetId,
          setupMode: mode,
          setupAction: action,
          releaseSha,
        });
      }
      if (action === 'dry-run') {
        const dryRunToast = getDryRunToast(setupResponse.setup, 'Setup dry-run');
        onNotify(dryRunToast.message, dryRunToast.severity);
      } else {
        onNotify(`Setup ${action === 'check' ? 'check' : 'run'} triggered successfully`, 'success');
      }
      setSetupDialogOpen(false);
      setSelectedTarget(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; code?: string } } };
      if (err?.response?.data?.code === 'LOCK_CONFLICT') {
        onNotify(err.response?.data?.message ?? 'Server is locked by another operation', 'error');
      } else {
        onNotify(
          action === 'dry-run'
            ? getRequestFailureMessage(
              e,
              'Failed to request setup dry-run',
              'Setup dry-run request timed out before the backend responded. Refresh the setup runs list in a moment.'
            )
            : getRequestFailureMessage(e, 'Failed to trigger setup'),
          'error'
        );
      }
    } finally {
      await refreshTargetsAndData(true);
      setActionLoading(false);
    }
  };

  const handleReprovision = async (target: DeploymentTargetRecord) => {
    setActionLoading(true);
    try {
      await deploymentDashboardApi.reprovisionCredentials(target._id, {
        setupMode: resolvePreferredSetupMode(target),
        reason: 'Triggered from dashboard',
      });
      onNotify('Credential re-provision triggered', 'success');
    } catch (e: unknown) {
      onNotify(getRequestFailureMessage(e, 'Failed to trigger credential re-provision'), 'error');
    } finally {
      await refreshTargetsAndData(true);
      setActionLoading(false);
    }
  };

  const loadManagedArtifacts = useCallback(async (targetId?: string) => {
    const resolvedTargetId = targetId || activeManagedTargetIdRef.current;
    if (!resolvedTargetId || resolvedTargetId !== activeManagedTargetIdRef.current) {
      return;
    }

    const requestId = ++managedArtifactsRequestRef.current;
    setManagedArtifactsLoading(true);
    try {
      const response = await deploymentDashboardApi.getManagedArtifacts(resolvedTargetId);
      if (requestId === managedArtifactsRequestRef.current) {
        setManagedArtifacts(response.data ?? []);
      }
    } catch (e: unknown) {
      if (requestId === managedArtifactsRequestRef.current) {
        const err = e as { response?: { data?: { message?: string } } };
        onNotify(err?.response?.data?.message ?? 'Failed to load managed files', 'error');
      }
    } finally {
      if (requestId === managedArtifactsRequestRef.current) {
        setManagedArtifactsLoading(false);
      }
    }
  }, [onNotify]);

  const handleOpenManagedArtifacts = useCallback((target: DeploymentTargetRecord) => {
    activeManagedTargetIdRef.current = target._id;
    setManagedArtifactsTarget(target);
    setManagedArtifacts([]);
    setManagedArtifactsDialogOpen(true);
    void loadManagedArtifacts(target._id);
  }, [loadManagedArtifacts]);

  const handleReconcileManagedArtifacts = useCallback(async () => {
    if (!managedArtifactsTarget) {
      return;
    }
    setActionLoading(true);
    let actionFailure: { message: string; severity: 'warning' | 'error' } | null = null;
    try {
      const response = await deploymentDashboardApi.reconcileManagedArtifacts(managedArtifactsTarget._id);
      const setupStatus = response.data?.setup?.status;
      onNotify(
        response.data?.message || (
          setupStatus === 'success'
            ? 'Managed-file reconciliation completed'
            : 'Managed files were activated with administrator follow-up required'
        ),
        setupStatus === 'success' ? 'success' : 'warning'
      );
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; requiresAdmin?: boolean } } };
      actionFailure = {
        message: getRequestFailureMessage(e, 'Managed-file reconciliation failed'),
        severity: err?.response?.data?.requiresAdmin ? 'warning' : 'error',
      };
    } finally {
      await Promise.all([
        loadManagedArtifacts(managedArtifactsTarget._id),
        refreshTargetsAndData(true),
      ]);
      if (actionFailure) {
        onNotify(actionFailure.message, actionFailure.severity);
      }
      setActionLoading(false);
    }
  }, [loadManagedArtifacts, managedArtifactsTarget, onNotify, refreshTargetsAndData]);

  const handleTlsAction = useCallback(async (
    target: DeploymentTargetRecord,
    action: 'check' | 'issue' | 'renew-dry-run' | 'renew'
  ) => {
    setActionLoading(true);
    try {
      if (action === 'check') {
        await deploymentDashboardApi.checkTlsCertificate(target._id);
      } else if (action === 'issue') {
        await deploymentDashboardApi.issueTlsCertificate(target._id);
      } else if (action === 'renew-dry-run') {
        await deploymentDashboardApi.runTlsRenewalDryRun(target._id);
      } else {
        await deploymentDashboardApi.renewTlsCertificate(target._id);
      }
      const label = action === 'renew-dry-run'
        ? 'TLS renewal dry-run'
        : action === 'renew'
          ? 'TLS renewal'
          : action === 'issue'
            ? 'Certificate issuance'
            : 'Certificate check';
      onNotify(`${label} started`, 'success');
    } catch (e: unknown) {
      onNotify(getRequestFailureMessage(e, `TLS ${action} action failed`), 'error');
    } finally {
      await refreshTargetsAndData(true);
      setActionLoading(false);
    }
  }, [onNotify, refreshTargetsAndData]);

  const handleRefreshSetup = async (setupId: string) => {
    try {
      await deploymentDashboardApi.refreshSetup(setupId);
    } catch (e: unknown) {
      onNotify(getRequestFailureMessage(e, 'Failed to refresh setup status'), 'error');
    } finally {
      await refreshTargetsAndData(true);
    }
  };

  const loadTargetHistory = useCallback(async (summary: SetupTargetSummary, append = false) => {
    const existingState = targetHistoryById[summary.targetId];

    setTargetHistoryById((current) => ({
      ...current,
      [summary.targetId]: {
        items: append ? existingState?.items || [] : [],
        total: existingState?.total || 0,
        loaded: append ? Boolean(existingState?.loaded) : false,
        error: '',
        loading: true,
      },
    }));

    try {
      const response = await deploymentDashboardApi.getSetupHistory({
        targetId: summary.targetId,
        supersededOnly: true,
        limit: SETUP_HISTORY_PAGE_SIZE,
        skip: append ? existingState?.items.length || 0 : 0,
      });

      setTargetHistoryById((current) => ({
        ...current,
        [summary.targetId]: {
          items: append ? [...(existingState?.items || []), ...response.data] : response.data,
          total: response.total,
          loaded: true,
          loading: false,
          error: '',
        },
      }));
    } catch (e: unknown) {
      setTargetHistoryById((current) => ({
        ...current,
        [summary.targetId]: {
          items: append ? existingState?.items || [] : [],
          total: existingState?.total || 0,
          loaded: Boolean(existingState?.loaded),
          loading: false,
          error: getRequestFailureMessage(
            e,
            'Failed to load setup history',
            'Setup history request timed out before the backend responded.'
          ),
        },
      }));
    }
  }, [targetHistoryById]);

  const loadStackHistory = useCallback(async (summary: SetupStackSummary, append = false) => {
    const existingState = stackHistoryByKey[summary.key];
    const customerId = typeof summary.customer === 'string' ? summary.customer : summary.customer?._id || '';
    const serverId = typeof summary.server === 'string' ? summary.server : summary.server?._id || '';

    if (!summary.stackId || !customerId || !serverId || !summary.environment) {
      setStackHistoryByKey((current) => ({
        ...current,
        [summary.key]: {
          items: existingState?.items || [],
          total: existingState?.total || 0,
          loaded: Boolean(existingState?.loaded),
          loading: false,
          error: 'Stack history scope is incomplete for this record.',
        },
      }));
      return;
    }

    setStackHistoryByKey((current) => ({
      ...current,
      [summary.key]: {
        items: append ? existingState?.items || [] : [],
        total: existingState?.total || 0,
        loaded: append ? Boolean(existingState?.loaded) : false,
        error: '',
        loading: true,
      },
    }));

    try {
      const response = await deploymentDashboardApi.getStackHistory({
        stackId: summary.stackId,
        customerId,
        serverId,
        environment: summary.environment,
        supersededOnly: true,
        limit: SETUP_HISTORY_PAGE_SIZE,
        skip: append ? existingState?.items.length || 0 : 0,
      });

      setStackHistoryByKey((current) => ({
        ...current,
        [summary.key]: {
          items: append ? [...(existingState?.items || []), ...response.data] : response.data,
          total: response.total,
          loaded: true,
          loading: false,
          error: '',
        },
      }));
    } catch (e: unknown) {
      setStackHistoryByKey((current) => ({
        ...current,
        [summary.key]: {
          items: append ? existingState?.items || [] : [],
          total: existingState?.total || 0,
          loaded: Boolean(existingState?.loaded),
          loading: false,
          error: getRequestFailureMessage(
            e,
            'Failed to load stack history',
            'Stack history request timed out before the backend responded.'
          ),
        },
      }));
    }
  }, [stackHistoryByKey]);

  const loadRunDetails = useCallback(async (setupId: string) => {
    activeDetailsIdRef.current = setupId;
    setDetailsLoading(true);
    try {
      const response = await deploymentDashboardApi.getSetup(setupId);
      if (activeDetailsIdRef.current === setupId) {
        setSelectedRunDetails(response.setup);
      }
    } catch {
      if (activeDetailsIdRef.current === setupId) {
        onNotify('Failed to load run details', 'error');
      }
    } finally {
      if (activeDetailsIdRef.current === setupId) {
        setDetailsLoading(false);
      }
    }
  }, [onNotify]);

  const handleOpenRunDetails = useCallback((setup: SetupRecord) => {
    setSelectedRunDetails(setup);
    setDetailsDialogOpen(true);
    void loadRunDetails(setup._id);
  }, [loadRunDetails]);

  const handleRefreshRunDetails = useCallback(async () => {
    if (!selectedRunDetails?._id) {
      return;
    }
    await loadRunDetails(selectedRunDetails._id);
  }, [loadRunDetails, selectedRunDetails]);

  const handleForceRelease = async (lockId: string, reason: string) => {
    setActionLoading(true);
    try {
      await deploymentDashboardApi.forceReleaseLock(lockId, reason);
      onNotify('Lock force-released', 'success');
      setForceReleaseDialogOpen(false);
      setSelectedLock(null);
      await refreshTargetsAndData(true);
    } catch {
      onNotify('Failed to force-release lock', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartStack = (stack: StackCandidate) => {
    setSelectedStack(stack);
    setStackDialogOpen(true);
  };

  const handleConfirmStack = async (payload: {
    action: 'stack-run' | 'stack-dry-run';
    stackId: string;
    customerId: string;
    serverId: string;
    environment: string;
    modeOverrides: Record<string, string>;
    reason: string;
  }) => {
    setActionLoading(true);
    try {
      let stackRunResponse;
      if (payload.action === 'stack-dry-run') {
        const { action, ...requestPayload } = payload;
        stackRunResponse = await deploymentDashboardApi.createStackDryRun(requestPayload);
      } else {
        const { action, ...requestPayload } = payload;
        stackRunResponse = await deploymentDashboardApi.createStackRun(requestPayload);
      }
      if (payload.action === 'stack-dry-run') {
        const dryRunToast = getDryRunToast(stackRunResponse.stackRun, 'Stack dry-run');
        onNotify(dryRunToast.message, dryRunToast.severity);
      } else {
        onNotify('Stack run triggered successfully', 'success');
      }
      setStackDialogOpen(false);
      setSelectedStack(null);
      await refreshTargetsAndData(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; code?: string } } };
      if (err?.response?.data?.code === 'LOCK_CONFLICT') {
        onNotify(err.response?.data?.message ?? 'Server is locked by another operation', 'error');
      } else {
        onNotify(
          payload.action === 'stack-dry-run'
            ? getRequestFailureMessage(
              e,
              'Failed to request stack dry-run',
              'Stack dry-run request timed out before the backend responded. Refresh the stack runs list in a moment.'
            )
            : getRequestFailureMessage(e, 'Failed to trigger stack run', 'Stack run request timed out before the backend responded.'),
          'error'
        );
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryStack = async (stackRunId: string) => {
    setActionLoading(true);
    try {
      await deploymentDashboardApi.retryStackRun(stackRunId, { reason: 'Retry triggered from dashboard' });
      onNotify('Stack retry triggered successfully', 'success');
      await refreshTargetsAndData(true);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; code?: string } } };
      if (err?.response?.data?.code === 'LOCK_CONFLICT') {
        onNotify(err.response?.data?.message ?? 'Server is locked by another operation', 'error');
      } else {
        onNotify(err?.response?.data?.message ?? 'Failed to retry stack run', 'error');
      }
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <SetupExplorer
        customers={availableCustomers}
        servers={availableServers}
        groups={serverGroups}
        loading={loading}
        error={error || ''}
        saving={actionLoading}
        isAdmin={isAdmin}
        targetHistoryById={targetHistoryById}
        stackHistoryByKey={stackHistoryByKey}
        onRefresh={() => { void refreshTargetsAndData(); }}
        onLoadTargetHistory={(summary, append) => { void loadTargetHistory(summary, append); }}
        onLoadStackHistory={(summary, append) => { void loadStackHistory(summary, append); }}
        onOpenSetup={handleStartSetup}
        onReprovision={(target) => { void handleReprovision(target); }}
        onOpenManagedArtifacts={handleOpenManagedArtifacts}
        onTlsAction={(target, action) => { void handleTlsAction(target, action); }}
        onRefreshSetup={(setupId) => { void handleRefreshSetup(setupId); }}
        onOpenRunDetails={handleOpenRunDetails}
        onOpenForceRelease={(lock) => { setSelectedLock(lock); setForceReleaseDialogOpen(true); }}
        onOpenStack={handleStartStack}
        onRetryStack={(stackRunId) => { void handleRetryStack(stackRunId); }}
      />

      <StartSetupDialog
        open={setupDialogOpen}
        target={selectedTarget}
        latestSetup={selectedTargetLatestSetup}
        onClose={() => { setSetupDialogOpen(false); setSelectedTarget(null); }}
        onConfirm={handleConfirmSetup}
        loading={actionLoading}
      />

      <ForceReleaseDialog
        open={forceReleaseDialogOpen}
        lock={selectedLock}
        onClose={() => { setForceReleaseDialogOpen(false); setSelectedLock(null); }}
        onConfirm={handleForceRelease}
        loading={actionLoading}
      />

      <StartStackDialog
        open={stackDialogOpen}
        stack={selectedStack}
        onClose={() => { setStackDialogOpen(false); setSelectedStack(null); }}
        onConfirm={handleConfirmStack}
        loading={actionLoading}
      />

      <SetupRunDetailsDialog
        open={detailsDialogOpen}
        setup={selectedRunDetails}
        loading={detailsLoading}
        onClose={() => {
          activeDetailsIdRef.current = null;
          setDetailsLoading(false);
          setDetailsDialogOpen(false);
          setSelectedRunDetails(null);
        }}
        onRefresh={handleRefreshRunDetails}
      />

      <ManagedArtifactsDialog
        open={managedArtifactsDialogOpen}
        target={managedArtifactsTarget}
        artifacts={managedArtifacts}
        loading={managedArtifactsLoading}
        actionLoading={actionLoading}
        isAdmin={isAdmin}
        onClose={() => {
          activeManagedTargetIdRef.current = null;
          managedArtifactsRequestRef.current += 1;
          setManagedArtifactsLoading(false);
          setManagedArtifactsDialogOpen(false);
          setManagedArtifactsTarget(null);
          setManagedArtifacts([]);
        }}
        onReload={() => loadManagedArtifacts()}
        onReconcile={handleReconcileManagedArtifacts}
      />
    </Box>
  );
};

export default SetupPanel;
